import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaFileInvoice, FaListAlt, FaCog, FaCheckCircle, FaTimesCircle, FaChevronRight, FaCertificate } from 'react-icons/fa';
import { getEstado } from '../../services/facturadorService';
import { FALTANTES, NOMBRES_TIPO, fechaCorta, fechaChile, mensajeError } from './comun';
import '../../styles/pages/Facturador.css';

const accesos = [
  {
    title: 'Emitir documento',
    description: 'Factura, factura exenta o factura de compra; se envía al SII en el acto',
    icon: <FaFileInvoice />,
    path: '/facturador/emitir',
    color: '#2b6cb0',
  },
  {
    title: 'Documentos',
    description: 'Estados del SII, PDF y XML; desde aquí se anula o corrige con notas',
    icon: <FaListAlt />,
    path: '/facturador/documentos',
    color: '#27ae60',
  },
  {
    title: 'Configuración',
    description: 'Datos del emisor, certificado digital y folios (CAF)',
    icon: <FaCog />,
    path: '/facturador/configuracion',
    color: '#e67e22',
  },
];

const FacturadorHub = ({ id_company }) => {
  const navigate = useNavigate();
  const [estado, setEstado] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getEstado(id_company)
      .then(setEstado)
      .catch((err) => setError(mensajeError(err, 'No se pudo cargar el facturador')));
  }, [id_company]);

  const encabezado = (
    <header className="fe-header">
      <button className="fe-back-btn" onClick={() => navigate('/dashboard')}>
        <FaArrowLeft /> Volver al Dashboard
      </button>
      <div className="fe-header-titulo">
        <div>
          <h1 className="fe-title">Facturación electrónica</h1>
          <p className="fe-sub">Emite tus documentos tributarios electrónicos aquí o pídeselos a Renata por WhatsApp</p>
        </div>
        {estado?.activado && (
          <span className={`fe-badge ${estado.ambiente === 'produccion' ? 'fe-badge--verde' : 'fe-badge--amarillo'}`}>
            {estado.ambiente === 'produccion' ? 'Producción' : 'Certificación (pruebas del SII)'}
          </span>
        )}
      </div>
    </header>
  );

  if (error) {
    return (
      <div className="fe-page fe-portada">
        {encabezado}
        <div className="fe-aviso fe-aviso--rojo">{error}</div>
      </div>
    );
  }
  if (!estado) {
    return (
      <div className="fe-page fe-portada">
        {encabezado}
        <p className="fe-loading">Cargando...</p>
      </div>
    );
  }
  if (!estado.activado) {
    return (
      <div className="fe-page fe-portada">
        {encabezado}
        <div className="fe-aviso fe-aviso--azul">
          El facturador electrónico no está activado para esta empresa. Pídele a EFE que lo active.
        </div>
      </div>
    );
  }

  const { uso, certificado } = estado;
  const sobreCupo = uso.cupo > 0 && uso.usados >= uso.cupo;
  const cercaDelCupo = uso.cupo > 0 && uso.usados >= Math.ceil(uso.cupo * 0.8);
  const requisitos = Object.keys(FALTANTES);
  const listos = requisitos.filter((clave) => !estado.faltantes.includes(clave)).length;

  return (
    <div className="fe-page fe-portada">
      {encabezado}

      {!estado.listo && (
        <div className="fe-panel">
          <div className="fe-panel-cabecera">
            <h2 className="fe-panel-title">Qué falta para emitir</h2>
            <span className="fe-muted">{listos} de {requisitos.length} listos</span>
          </div>
          <div className="fe-barra">
            <div className="fe-barra-relleno" style={{ width: `${Math.round((listos / requisitos.length) * 100)}%` }} />
          </div>
          <ul className="fe-checklist fe-checklist--columnas">
            {Object.entries(FALTANTES).map(([clave, info]) => {
              const falta = estado.faltantes.includes(clave);
              return (
                <li key={clave} className={falta ? 'fe-check-item--falta' : undefined}>
                  {falta ? <FaTimesCircle className="fe-check-falta" /> : <FaCheckCircle className="fe-check-ok" />}
                  <span>{info.texto}</span>
                  {falta && info.ruta && <Link to={info.ruta} className="fe-completar">Completar</Link>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="fe-accesos">
        {accesos.map((item) => (
          <button key={item.path} type="button" className="fe-acceso" onClick={() => navigate(item.path)}>
            <span className="fe-acceso-icono" style={{ color: item.color, background: `${item.color}1a` }}>
              {item.icon}
            </span>
            <span className="fe-acceso-texto">
              <strong>{item.title}</strong>
              <span>{item.description}</span>
            </span>
            <FaChevronRight className="fe-acceso-flecha" />
          </button>
        ))}
      </div>

      <div className="fe-portada-columnas">
        <div className="fe-panel">
          <h2 className="fe-panel-title">Uso del mes</h2>
          <p className="fe-uso-cifra">
            <strong>{uso.usados}</strong>
            {uso.cupo > 0 ? `de ${uso.cupo} documentos incluidos` : uso.usados === 1 ? 'documento emitido' : 'documentos emitidos'}
          </p>
          {uso.cupo > 0 && (
            <div className="fe-barra">
              <div
                className={`fe-barra-relleno ${sobreCupo ? 'fe-barra-relleno--rojo' : cercaDelCupo ? 'fe-barra-relleno--amarillo' : ''}`}
                style={{ width: `${Math.min(100, Math.round((uso.usados / uso.cupo) * 100))}%` }}
              />
            </div>
          )}
          <dl className="fe-datos-lista">
            <div>
              <dt>Adicionales</dt>
              <dd>{uso.adicionales}</dd>
            </div>
            <div>
              <dt>Tope del mes</dt>
              <dd>{uso.tope ?? 'Sin tope'}</dd>
            </div>
          </dl>
          <p className="fe-hint">Cuentan todos los documentos del mes, incluidas las notas, salvo los rechazados por el SII.</p>
        </div>

        <div className="fe-panel">
          <h2 className="fe-panel-title">Folios disponibles</h2>
          <table className="fe-folios">
            <thead>
              <tr>
                <th>Documento</th>
                <th className="fe-num">Folios</th>
                <th className="fe-num">Vencen</th>
              </tr>
            </thead>
            <tbody>
              {estado.folios.map((f) => (
                <tr key={f.tipo}>
                  <td>{NOMBRES_TIPO[f.tipo] || f.tipo}</td>
                  <td className={`fe-num ${f.disponibles === 0 ? 'fe-sin-folios' : ''}`}>
                    {f.disponibles === 0 ? 'Sin folios' : f.disponibles}
                  </td>
                  <td className="fe-num fe-muted">{f.vence_el ? fechaCorta(f.vence_el) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={`fe-certificado ${!certificado || certificado.vencido ? 'fe-certificado--falta' : ''}`}>
            <FaCertificate />
            <div>
              <strong>{certificado ? certificado.nombre_titular : 'Sin certificado digital'}</strong>
              <span>
                {certificado
                  ? `Certificado digital · ${certificado.vencido ? 'venció' : 'vence'} el ${fechaChile(certificado.fecha_vencimiento)}`
                  : 'Súbelo en Configuración para poder firmar'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacturadorHub;
